/**
 * Trash: seeded chute spawner + registry. Every piece is grabbable, portal-traversable, and
 * incinerable. Overflow pressure = live piece count.
 *
 * Rendering is instanced: one InstancedMesh per kind (constant draw calls
 * regardless of piece count). Each piece keeps a lightweight Object3D
 * transform proxy (`mesh`) that the rest of the game reads/writes; matrices
 * are composed into the instance buffers once per render frame.
 */
import * as THREE from "three";
import type RAPIER from "@dimforge/rapier3d-compat";
import type { ChuteId } from "../world/ArenaDefinition.js";
import type { ReceiverTier } from "../simulation/types.js";
import { TRASH_THEME } from "../render/theme.js";
import { TRASH_COLLIDER_ENVELOPES, createTrashModels } from "../render/TrashModels.js";

export type TrashKind = "crate" | "can" | "green" | "red" | "husk";

export interface TrashPiece {
  id: number;
  kind: TrashKind;
  body: RAPIER.RigidBody;
  /** transform proxy — NOT in the scene graph; drives an instance slot */
  mesh: THREE.Object3D;
  live: boolean;
  held: boolean;
  bulk: number;
  fuseTicks: number | null;
  loadId: string | null;
  /**
   * Position of this piece inside its release volley; -1 when not part of a
   * recorded load (LOCKED-DESIGN 18.14). The sim jams
   * `bulkOf(record.pieces.slice(record.delivered))` — an INDEX-based rule — so
   * every physical reject reconciliation must clear by index too. Clearing
   * chamber-preferred or by ascending id would strand `laneJamBulk` above the
   * feedable physical bulk on a mixed-bulk load: the exact phantom-jam class
   * 18.12 eliminated. Cleared to -1 everywhere `loadId` is cleared.
   */
  loadIndex: number;
  rejected: boolean;
  rejectReady: boolean;
  chamber: boolean;
  /**
   * The receiver lane this piece is jammed against (LOCKED-DESIGN 18.12 feed-the-
   * maw): stamped when a piece becomes a jam-pile reject, from load.route.receiver.
   * Survives grabs AND red-detonation (husk keeps its lane + 3 bulk). It marks the
   * ONE piece that owes a terminal `feed` action — incinerated at the maw, or a
   * returned-feed when it physically leaves the chamber — so laneJamBulk can never
   * strand above the physical pile. Cleared exactly when that feed is issued.
   */
  jamLane: ReceiverTier | null;
  /** seconds until this piece may traverse a portal again */
  portalCooldown: number;
  /**
   * Seconds this piece may still use the portal's WIDENED `"inward"` capture
   * window (PLAYTEST F5, LOCKED-DESIGN 18.17). Stamped by `GrabBeam` only on a
   * release whose solve origin it actually re-clamped in front of an aimed
   * disc — the point-blank throw the widening exists for — and decayed by
   * `updateCooldowns`. Zero (the default, and the value for every other throw)
   * means the historical strict capture window, so debris that merely drifts
   * behind a panel is never delivered.
   */
  portalInward: number;
  /** re-arm gate: must leave both portal capture volumes after a traversal */
  portalArmed: boolean;
  prevPos: THREE.Vector3;
  prevQuat: THREE.Quaternion;
}

const DROP_HEIGHT = 15;
const DROP_ZONE = { minX: -11, maxX: 11, minZ: -4, maxZ: 10 };
const INSTANCE_CAP = 512; // render buffer ceiling (stress-test headroom)

/**
 * Soft-CCD lookahead for every trash body, in metres (PLAYTEST F16).
 *
 * A trash piece is the only dynamic body in the game and the fastest thing in
 * it. `GrabBeam.ballisticVelocity` solves every throw for a FIXED flight time
 * (`ARC_T_MAX` 1.35 s once the aim point is past 18.9 m), so launch speed grows
 * linearly with aim distance and is bounded only by how far the aim raycast can
 * reach across a 56 x 40 arena — measured 50.08 m/s at the extreme, and the
 * beam's own hold tracking clamps at 34 m/s. At the fixed 1/60 s step 50 m/s is
 * 0.835 m of travel per step, against a `glass-bulkhead` that is 0.32 m thick.
 *
 * 1.0 m is that worst case rounded up: soft CCD keeps contacts alive for
 * anything closing within one step's travel at 60 m/s, which covers the whole
 * reachable envelope with room to spare. It is a predictive-contact widening,
 * not substepping, so it is effectively free — 0.084 ms/step before, 0.087
 * ms/step after, measured over 600 hand-driven `world.step()` calls on the same
 * stress(150) scene (i.e. inside the run-to-run noise of the baseline itself).
 */
const SOFT_CCD_PREDICTION = 1;

const KINDS: readonly TrashKind[] = ["crate", "can", "green", "red", "husk"];
const BULK: Record<TrashKind, number> = { crate: 1, can: 2, green: 3, red: 3, husk: 3 };

export class TrashSpawner {
  readonly group = new THREE.Group();
  readonly pieces: TrashPiece[] = [];

  private nextId = 1;
  private warmupTimer = .15;
  private warmupIndex = 0;

  // Shared geometry/materials per kind (LOCKED-DESIGN 18.27 identity pass).
  // Each kind is ONE merged geometry with two groups; the material ARRAY maps
  // group 0 → body tokens, group 1 → trim tokens. The colliders in spawnAt are
  // untouched — TrashModels' factories are envelope-bounded visuals only, and
  // TrashModels.test.ts measures every vertex against those envelopes.
  private readonly models = createTrashModels();
  private readonly geo = {
    crate: this.models.crate.geometry,
    can: this.models.can.geometry,
    green: this.models.green.geometry,
    red: this.models.red.geometry,
    husk: this.models.husk.geometry,
  };
  private readonly mat = Object.fromEntries(
    (Object.keys(this.models) as TrashKind[]).map((kind) => [kind, [
      new THREE.MeshStandardMaterial(TRASH_THEME[kind].material),
      new THREE.MeshStandardMaterial(TRASH_THEME[kind].trim),
    ]]),
  ) as Record<TrashKind, [THREE.MeshStandardMaterial, THREE.MeshStandardMaterial]>;

  private readonly inst: Record<TrashKind, THREE.InstancedMesh>;
  private readonly scratchMat = new THREE.Matrix4();
  private readonly unitScale = new THREE.Vector3(1, 1, 1);

  constructor(
    private readonly api: typeof RAPIER,
    private readonly world: RAPIER.World,
    private readonly chuteOutlets: Record<ChuteId, { position: readonly [number, number, number]; velocity: readonly [number, number, number] }>,
  ) {
    const makeInst = (kind: TrashKind): THREE.InstancedMesh => {
      const m = new THREE.InstancedMesh(this.geo[kind], this.mat[kind], INSTANCE_CAP);
      m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      m.count = 0;
      m.castShadow = m.receiveShadow = true;
      m.frustumCulled = false; // instances move every frame
      this.group.add(m);
      return m;
    };
    this.inst = { crate: makeInst("crate"), can: makeInst("can"), green: makeInst("green"), red: makeInst("red"), husk: makeInst("husk") };
  }

  /** Normal Shift supply comes only from authoritative spawn events. */
  update(dt: number, _rampT: number): void { this.updateCooldowns(dt); }

  /** Deterministic tutorial-only drip; still uses the authored physical chutes. */
  updateWarmup(dt: number): void {
    this.updateCooldowns(dt);
    this.warmupTimer -= dt;
    if (this.warmupTimer > 0 || this.pieces.length >= 5) return;
    const chutes: readonly ChuteId[] = ["left", "center", "right"];
    const kinds = ["crate", "can", "green"] as const;
    const index = this.warmupIndex++;
    this.spawnScheduled(kinds[index % kinds.length]!, chutes[index % chutes.length]!);
    this.warmupTimer = 1.1;
  }

  updateCooldowns(dt: number): void {
    for (const p of this.pieces) {
      if (p.portalCooldown > 0) p.portalCooldown -= dt;
      // The point-blank inward window decays with the throw that opened it.
      if (p.portalInward > 0) p.portalInward -= dt;
    }
  }

  liveCount(): number {
    return this.pieces.length; // dead pieces are evicted on consume
  }

  /** Stress/debug only: dump n pieces from the sky, bounded by the instance buffer. */
  spawnBurst(n: number): void {
    const cap = Math.min(n, INSTANCE_CAP - this.pieces.length);
    for (let i = 0; i < cap; i++) this.spawnSky();
  }

  private spawnSky(): TrashPiece {
    const kind: TrashKind = Math.random() < .5 ? "crate" : Math.random() < .6 ? "can" : "green";
    return this.spawnAt(kind, null);
  }

  private spawnAt(kind: TrashKind, outlet: { position: readonly [number, number, number]; velocity: readonly [number, number, number] } | null): TrashPiece {
    const id = this.nextId++;
    const x = outlet?.position[0] ?? DROP_ZONE.minX + Math.random() * (DROP_ZONE.maxX - DROP_ZONE.minX);
    const y = outlet?.position[1] ?? DROP_HEIGHT;
    const z = outlet?.position[2] ?? DROP_ZONE.minZ + Math.random() * (DROP_ZONE.maxZ - DROP_ZONE.minZ);

    // Chute feeds tumble. The west/east SIDE chutes (horizontal launch) roll
    // FORWARD along their launch so a piece always tumbles INTO the room, never
    // back toward its wall (Phase-4F chute-framing fix, LOCKED-DESIGN 18.12):
    // the old id-only spin rolled one side's feed straight back at its wall
    // because the fixed world-space spin was not mirror-symmetric. The center
    // (south-wall) chute keeps the original id-based tumble. Deterministic
    // (launch sign + id), so replay parity is unchanged.
    const angular = outlet
      ? Math.abs(outlet.velocity[0]) > Math.abs(outlet.velocity[2])
        ? { x: ((id * 3) % 7 - 3) * .14, y: ((id * 5) % 11 - 5) * .18, z: Math.sign(outlet.velocity[0]) * 2.2 + ((id * 7) % 5 - 2) * .12 }
        : { x: ((id * 3) % 9 - 4) * .28, y: ((id * 5) % 11 - 5) * .24, z: ((id * 7) % 9 - 4) * .28 }
      : { x: (Math.random() - .5) * 4, y: (Math.random() - .5) * 4, z: (Math.random() - .5) * 4 };
    // Tunnel-proofing (PLAYTEST F16: "had a block that i threw miss the portal
    // and then clip through the glass into the incinerator anyway"). It is not
    // a portal or a scoring bug — it is discrete-integration tunnelling through
    // the 0.32 m `glass-bulkhead`, and CCD was off on every body in the game.
    // Measured floors with both knobs off, sweeping the wall head-on across all
    // 24 sub-step launch phases: can 33 m/s, crate 37, green 37 — under or at
    // the 34 m/s the beam's own hold tracking reaches and well under the 50.08
    // m/s a long throw launches at. 305 of 49126 trials in the 20-40 m/s band
    // ended up chamber-side with no portal traversal — and the owner's exact
    // throw reproduces through the game's OWN release path: 2 of 4047 real
    // grab-and-release throws (a can, standing 7 m in front of the glass at
    // (±4,1.4,−6), aiming long over the chamber, launch 34.22 m/s, closing at
    // 32.04 m/s) landed in the chamber with `run.portalTraversals` unmoved.
    //
    // BOTH knobs, because they fail in opposite directions and neither alone is
    // a seal (all numbers from the same swept probe):
    //  - soft CCD alone leaks the moment a step's travel exceeds
    //    SOFT_CCD_PREDICTION — first tunnel at exactly 62 m/s = 1.03 m/step;
    //  - hard CCD alone leaks when the piece spends its single TOI substep on
    //    another surface first: 2 of 54430, all of them a can already sliding on
    //    the floor when it reached the glass. Raising `maxCcdSubsteps` would
    //    also close that, but that is a world-wide integration parameter and
    //    this is a trash-body problem.
    // Together: 0 of 114048 trials from 20 to 62 m/s, across all three shapes,
    // three yaws, two spins, free flight and floor-sliding.
    //
    // This is an Engine-layer body property. The simulation kernel never reads
    // a body pose (parity is FNV-1a over the ledger), so it moves no seed hash
    // and needs no GAME_VERSION bump. Portal capture is likewise untouched:
    // `PortalPair.computeTraversal` offsets its crossing plane OUTWARD by
    // `bodyRadius` and tests a prev/curr sign change, so it never depended on a
    // piece penetrating anything, and no portal disc has a collider for CCD to
    // stop a piece against.
    const bodyDesc = this.api.RigidBodyDesc.dynamic()
      .setTranslation(x, y, z)
      .setLinearDamping(0.1)
      .setAngularDamping(0.4)
      .setSoftCcdPrediction(SOFT_CCD_PREDICTION)
      .setCcdEnabled(true)
      .setAngvel(angular);
    const body = this.world.createRigidBody(bodyDesc);
    if (outlet) body.setLinvel({ x: outlet.velocity[0], y: outlet.velocity[1], z: outlet.velocity[2] }, true);
    // Collider values come from the ONE envelope table the visual factories
    // are bounded by and the geometry tests measure against (18.27 review
    // finding: a hand-mirrored copy here let a retune silently strand the
    // visuals outside the real collider). Values are unchanged: cuboid 0.425 /
    // cylinder 0.4x0.35 / ball 0.45 / ball 0.48.
    const E = TRASH_COLLIDER_ENVELOPES;
    const colliderDesc =
      kind === "crate"
        ? this.api.ColliderDesc.cuboid(E.crate.halfExtent, E.crate.halfExtent, E.crate.halfExtent).setDensity(40)
        : kind === "can"
          ? this.api.ColliderDesc.cylinder(E.can.halfHeight, E.can.radius).setDensity(30)
          : this.api.ColliderDesc.ball(E[kind].radius).setDensity(25);
    colliderDesc.setFriction(0.7).setRestitution(kind === "green" ? 0.78 : 0.15);
    this.world.createCollider(colliderDesc, body);

    const mesh = new THREE.Object3D();
    mesh.position.set(x, y, z);

    const piece: TrashPiece = {
      id,
      kind,
      body,
      mesh,
      live: true,
      held: false,
      bulk: BULK[kind],
      fuseTicks: null,
      loadId: null,
      loadIndex: -1,
      rejected: false,
      rejectReady: false,
      chamber: false,
      jamLane: null,
      portalCooldown: 0,
      portalInward: 0,
      portalArmed: true,
      prevPos: new THREE.Vector3(x, y, z),
      prevQuat: new THREE.Quaternion(),
    };
    this.pieces.push(piece);
    return piece;
  }


  spawnScheduled(kind: Exclude<TrashKind, "husk">, chute: ChuteId): TrashPiece {
    return this.spawnAt(kind, this.chuteOutlets[chute]);
  }

  detonate(piece: TrashPiece): void {
    if (!piece.live || piece.kind !== "red") return;
    piece.kind = "husk";
    piece.bulk = 3;
    piece.fuseTicks = null;
  }

  /** Remove a piece from play (incinerated). Swap-pop keeps loops O(live). */
  consume(piece: TrashPiece): void {
    if (!piece.live) return;
    piece.live = false;
    piece.held = false;
    this.world.removeRigidBody(piece.body);
    const i = this.pieces.indexOf(piece);
    if (i >= 0) {
      const last = this.pieces.pop()!;
      if (last !== piece) this.pieces[i] = last;
    }
  }

  /** Interpolation history — call BEFORE world.step(). */
  storePrev(): void {
    for (const p of this.pieces) {
      const t = p.body.translation();
      const r = p.body.rotation();
      p.prevPos.set(t.x, t.y, t.z);
      p.prevQuat.set(r.x, r.y, r.z, r.w);
    }
  }

  syncMeshes(alpha: number, scratchPos: THREE.Vector3, scratchQuat: THREE.Quaternion): void {
    const counts: Record<TrashKind, number> = { crate: 0, can: 0, green: 0, red: 0, husk: 0 };
    for (const p of this.pieces) {
      const t = p.body.translation();
      const r = p.body.rotation();
      scratchPos.set(t.x, t.y, t.z);
      scratchQuat.set(r.x, r.y, r.z, r.w);
      p.mesh.position.lerpVectors(p.prevPos, scratchPos, alpha);
      p.mesh.quaternion.slerpQuaternions(p.prevQuat, scratchQuat, alpha);

      const slot = counts[p.kind]++;
      if (slot < INSTANCE_CAP) {
        this.scratchMat.compose(p.mesh.position, p.mesh.quaternion, this.unitScale);
        this.inst[p.kind].setMatrixAt(slot, this.scratchMat);
      }
    }
    for (const kind of KINDS) {
      const m = this.inst[kind];
      m.count = Math.min(counts[kind], INSTANCE_CAP);
      m.instanceMatrix.needsUpdate = true;
    }
  }

  reset(): void {
    for (const p of this.pieces) {
      p.live = false;
      this.world.removeRigidBody(p.body);
    }
    this.pieces.length = 0;
    this.nextId = 1;
    this.warmupTimer = .15;
    this.warmupIndex = 0;
    for (const kind of KINDS) this.inst[kind].count = 0;
  }
}
